import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  MessageSquare,
  Check,
  X,
  Clock,
  AlertTriangle,
  DollarSign,
  Calendar,
  Loader2,
  Plus,
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import {
  useDisputes,
  useDispute,
  useDisputeStats,
  useResolveDispute,
  useRejectDispute,
  useMarkDisputeInProgress,
} from '@/api-services/hooks/useDispute';
import { DisputeDTO, DisputeStatus, DisputeType, DisputePriority, RaisedByType, DisputeStats } from '@/api-services/hooks/disputeTypes';
import { useQueryClient } from '@tanstack/react-query';
import { PagedData } from '@/api-services/hooks/types';

const DisputesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | 'all'>('all');
  const [disputeTypeFilter, setDisputeTypeFilter] = useState<DisputeType | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedDisputeId, setSelectedDisputeId] = useState<number | null>(null);
  const [resolutionText, setResolutionText] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Build query params
  const queryParams = {
    page: currentPage,
    size: itemsPerPage,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    disputeType: disputeTypeFilter !== 'all' ? disputeTypeFilter : undefined,
  };

  // Fetch disputes
  const {
    data: disputesData,
    loading: disputesLoading,
    error: disputesError,
    refetch: refetchDisputes,
  } = useDisputes(queryParams);

  // Fetch stats
  const {
    data: statsData,
    loading: statsLoading,
  } = useDisputeStats();

  // Fetch selected dispute details
  const {
    data: selectedDispute,
    loading: disputeLoading,
  } = useDispute(selectedDisputeId || 0, {
    enabled: !!selectedDisputeId,
  });

  // Mutations
  const resolveMutation = useResolveDispute({
    onSuccess: () => {
      toast.success('Dispute resolved successfully');
      setResolveDialogOpen(false);
      setResolutionText('');
      queryClient.invalidateQueries({ queryKey: ['disputes'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-stats'] });
      if (selectedDisputeId) {
        queryClient.invalidateQueries({ queryKey: ['dispute', selectedDisputeId] });
      }
    },
    onError: (error) => {
      toast.error(`Failed to resolve dispute: ${error.message}`);
    },
  });

  const rejectMutation = useRejectDispute({
    onSuccess: () => {
      toast.success('Dispute rejected successfully');
      setRejectDialogOpen(false);
      setResolutionText('');
      queryClient.invalidateQueries({ queryKey: ['disputes'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-stats'] });
      if (selectedDisputeId) {
        queryClient.invalidateQueries({ queryKey: ['dispute', selectedDisputeId] });
      }
    },
    onError: (error) => {
      toast.error(`Failed to reject dispute: ${error.message}`);
    },
  });

  const markInProgressMutation = useMarkDisputeInProgress({
    onSuccess: () => {
      toast.success('Dispute marked as in progress');
      queryClient.invalidateQueries({ queryKey: ['disputes'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-stats'] });
      if (selectedDisputeId) {
        queryClient.invalidateQueries({ queryKey: ['dispute', selectedDisputeId] });
      }
    },
    onError: (error) => {
      toast.error(`Failed to update dispute: ${error.message}`);
    },
  });


  const getStatusBadge = (status?: DisputeStatus) => {
    const variants: Record<string, { className: string }> = {
      OPEN: { className: 'bg-destructive text-destructive-foreground' },
      IN_PROGRESS: { className: 'bg-yellow-500 text-yellow-50' },
      RESOLVED: { className: 'bg-green-500 text-green-50' },
      REJECTED: { className: 'bg-gray-500 text-gray-50' },
    };
    return variants[status || 'OPEN'] || variants.OPEN;
  };

  const getPriorityBadge = (priority?: string) => {
    const variants: Record<string, { className: string }> = {
      HIGH: { className: 'bg-destructive text-destructive-foreground' },
      MEDIUM: { className: 'bg-yellow-500 text-yellow-50' },
      LOW: { className: 'bg-gray-500 text-gray-50' },
      CRITICAL: { className: 'bg-red-600 text-red-50' },
    };
    return variants[priority || 'MEDIUM'] || variants.MEDIUM;
  };

  const getDisputeTypeIcon = (type?: DisputeType) => {
    switch (type) {
      case 'PAYMENT':
      case 'REFUND':
        return <DollarSign className="w-4 h-4" />;
      case 'DELIVERY':
        return <MessageSquare className="w-4 h-4" />;
      case 'PRODUCT':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const handleViewDetails = (disputeId: number) => {
    setSelectedDisputeId(disputeId);
    setDialogOpen(true);
  };

  const handleResolve = (disputeId: number) => {
    setSelectedDisputeId(disputeId);
    setResolveDialogOpen(true);
  };

  const handleReject = (disputeId: number) => {
    setSelectedDisputeId(disputeId);
    setRejectDialogOpen(true);
  };

  const handleMarkInProgress = (disputeId: number) => {
    markInProgressMutation.mutate(disputeId);
  };

  const handleConfirmResolve = () => {
    if (!selectedDisputeId || !resolutionText.trim()) {
      toast.error('Please provide a resolution');
      return;
    }
    resolveMutation.mutate({
      id: selectedDisputeId,
      resolution: resolutionText,
    });
  };

  const handleConfirmReject = () => {
    if (!selectedDisputeId || !resolutionText.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    rejectMutation.mutate({
      id: selectedDisputeId,
      resolution: resolutionText,
    });
  };

  // Filter disputes by search term
  const disputesList = disputesData as any;
  const filteredDisputes = disputesList?.content?.filter((dispute) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      dispute.disputeId?.toString().toLowerCase().includes(searchLower) ||
      dispute.bookingId?.toString().toLowerCase().includes(searchLower) ||
      dispute.reason?.toLowerCase().includes(searchLower) ||
      dispute.description?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, disputeTypeFilter]);

  const totalPages = disputesList?.totalPages || 0;
  const totalItems = disputesList?.totalElements || 0;

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };
  
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dispute Resolution</h1>
          <p className="text-muted-foreground mt-1">Manage and resolve customer disputes</p>
        </div>
        <Button onClick={() => navigate('/admin/disputes/add')} variant="default">
          <Plus className="w-4 h-4 mr-2" />
          Create Dispute
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="hover-lift interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Open Disputes</p>
                <p className="text-2xl font-bold text-destructive">
                  {statsLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    (statsData as DisputeStats | undefined)?.openDisputes || 0
                  )}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {statsLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    (statsData as DisputeStats | undefined)?.inProgressDisputes || 0
                  )}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-green-500">
                  {statsLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    (statsData as DisputeStats | undefined)?.resolvedDisputes || 0
                  )}
                </p>
              </div>
              <Check className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search disputes by ID, booking ID, reason, or description..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as DisputeStatus | 'all')}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={disputeTypeFilter}
              onValueChange={(value) => setDisputeTypeFilter(value as DisputeType | 'all')}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="PAYMENT">Payment</SelectItem>
                <SelectItem value="DELIVERY">Delivery</SelectItem>
                <SelectItem value="PRODUCT">Product</SelectItem>
                <SelectItem value="REFUND">Refund</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Disputes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Disputes ({totalItems})</CardTitle>
        </CardHeader>
        <CardContent>
          {disputesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : disputesError ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-destructive">Error loading disputes: {disputesError.message}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dispute ID</TableHead>
                      <TableHead>Booking ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Raised By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Raised At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDisputes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <p className="text-muted-foreground">No disputes found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDisputes.map((dispute) => {
                        const statusBadge = getStatusBadge(dispute.status);
                        const priorityBadge = getPriorityBadge(dispute.priority);

                        return (
                          <TableRow key={dispute.disputeId} className="hover:bg-muted/50">
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                {getDisputeTypeIcon(dispute.disputeType)}
                                <span className="font-medium">#{dispute.disputeId}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">#{dispute.bookingId}</span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{dispute.disputeType}</Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{dispute.reason}</p>
                                <p className="text-sm text-muted-foreground line-clamp-1">
                                  {dispute.description}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{dispute.raisedByType}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusBadge.className}>
                                {dispute.status?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {dispute.priority && (
                                <Badge className={priorityBadge.className}>
                                  {dispute.priority}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {dispute.raisedAt
                                ? new Date(dispute.raisedAt).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onSelect={() => handleViewDetails(dispute.disputeId!)}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  {dispute.status === 'OPEN' && (
                                    <DropdownMenuItem
                                      onSelect={() => handleMarkInProgress(dispute.disputeId!)}
                                    >
                                      <Clock className="w-4 h-4 mr-2" />
                                      Mark In Progress
                                    </DropdownMenuItem>
                                  )}
                                  {dispute.status !== 'RESOLVED' && dispute.status !== 'REJECTED' && (
                                    <>
                                      <DropdownMenuItem
                                        onSelect={() => handleResolve(dispute.disputeId!)}
                                      >
                                        <Check className="w-4 h-4 mr-2" />
                                        Resolve
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onSelect={() => handleReject(dispute.disputeId!)}
                                      >
                                        <X className="w-4 h-4 mr-2" />
                                        Reject
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 0 && (
                <DataTablePagination
                  currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  className="mt-4"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog for viewing dispute details */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          {disputeLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : selectedDispute ? (
            <>
              <DialogHeader>
                <DialogTitle>Dispute Details - #{selectedDispute.disputeId}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Booking ID</p>
                    <p className="font-medium">#{selectedDispute.bookingId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Dispute Type</p>
                    <Badge variant="outline">{selectedDispute.disputeType}</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Status</p>
                    <Badge className={getStatusBadge(selectedDispute.status).className}>
                      {selectedDispute.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Priority</p>
                    {selectedDispute.priority && (
                      <Badge className={getPriorityBadge(selectedDispute.priority).className}>
                        {selectedDispute.priority}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Raised By</p>
                    <Badge variant="outline">{selectedDispute.raisedByType}</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Raised At</p>
                    <p>
                      {selectedDispute.raisedAt
                        ? new Date(selectedDispute.raisedAt).toLocaleString()
                        : '-'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Reason</p>
                  <p className="font-medium">{selectedDispute.reason}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Description</p>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedDispute.description}</p>
                </div>

                {selectedDispute.resolution && (
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Resolution</p>
                    <p className="text-sm">{selectedDispute.resolution}</p>
                    {selectedDispute.resolvedAt && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Resolved at: {new Date(selectedDispute.resolvedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Resolve Dispute Dialog */}
      <Dialog 
        open={resolveDialogOpen} 
        onOpenChange={(open) => {
          setResolveDialogOpen(open);
          if (!open) {
            setResolutionText('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Dispute {selectedDisputeId ? `#${selectedDisputeId}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Resolution Details</label>
              <Textarea
                placeholder="Enter resolution details..."
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setResolveDialogOpen(false);
                setResolutionText('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmResolve}
              disabled={!selectedDisputeId || !resolutionText.trim() || resolveMutation.isPending}
            >
              {resolveMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resolving...
                </>
              ) : (
                'Resolve'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dispute Dialog */}
      <Dialog 
        open={rejectDialogOpen} 
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setResolutionText('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Dispute {selectedDisputeId ? `#${selectedDisputeId}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Rejection Reason</label>
              <Textarea
                placeholder="Enter rejection reason..."
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setRejectDialogOpen(false);
                setResolutionText('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={!selectedDisputeId || !resolutionText.trim() || rejectMutation.isPending}
              variant="destructive"
            >
              {rejectMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                'Reject'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisputesPage;
